import { mostReadable, TinyColor } from '@ctrl/tinycolor'
import { ComponentPropsWithoutRef, CSSProperties, ReactNode } from 'react'

import { cn } from '@sparkai/utils'

export interface BadgeProps extends ComponentPropsWithoutRef<'div'> {
  readonly children: ReactNode
  readonly size?: 'small' | 'large'
  readonly hasRemoveButton?: boolean
  readonly onRemove?: () => void
  readonly color?: BadgeColors
}

export type BadgeColors =
  | 'blue'
  | 'gray'
  | 'yellow'
  | 'red'
  | 'green'
  | 'black'
  | 'transparent'
  | 'deep-blue'
  | 'cherry'
  | 'emerald'
  | 'sunflower'
  | 'royal-blue'
  | 'tangerine'
  | 'purple'
  | 'cyan'
  | 'medium-gray'
  | 'lime'
  | 'magenta'
  | 'teal'
  | 'lavender'
  | 'bronze'
  | 'light-gray'
  | 'neon-green'
  | 'mint'
  | 'lemon'
  | 'peach'
  | 'midnight-blue'
  | 'white'

export const BADGE_COLORS = {
  '#00E': 'deep-blue',
  '#E6194B': 'cherry',
  '#3CB44B': 'emerald',
  '#FFEC70': 'sunflower',
  '#4363D8': 'royal-blue',
  '#F58231': 'tangerine',
  '#911EB4': 'purple',
  '#46F0F0': 'cyan',
  '#999': 'medium-gray',
  '#BCF60C': 'lime',
  '#C724B1': 'magenta',
  '#008080': 'teal',
  '#E6BEFF': 'lavender',
  '#9A6324': 'bronze',
  '#F5F5F5': 'light-gray',
  '#58FF36': 'neon-green',
  '#AAFFC3': 'mint',
  '#FFF000': 'lemon',
  '#FFD8B1': 'peach',
  '#000075': 'midnight-blue',
  '#FFFFFF': 'white',
}

const badgeColorClasses: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-800',
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  black: 'bg-black text-white',
  white: 'bg-white text-gray-600',
  transparent: '',
}

const badgeRemoveButtonClasses: Record<string, string> = {
  gray: 'text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:bg-gray-500',
  blue: 'text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:bg-blue-500',
  green: 'text-green-400 hover:bg-green-200 hover:text-green-500 focus:bg-green-500',
  red: 'text-red-400 hover:bg-red-200 hover:text-red-500 focus:bg-red-500',
  yellow: 'text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:bg-yellow-500',
}

const sizeClasses: Record<string, string> = {
  small: 'px-2 py-0.5 text-xs',
  large: 'px-2.5 py-0.5 text-sm',
}

const sizeRemoveClasses: Record<string, string> = {
  small: 'pr-0.5',
  large: 'pr-1',
}

export function Badge({
  children,
  size = 'small',
  hasRemoveButton,
  onRemove,
  className,
  color = 'gray',
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-inter font-medium break-all',
        sizeClasses[size],
        hasRemoveButton && sizeRemoveClasses[size],
        badgeColorClasses[color],
        className
      )}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      style={getStylesByColorName(color as any)}
      {...props}
    >
      {children}
      {hasRemoveButton && (
        <button
          type='button'
          onClick={onRemove}
          className={cn(
            'ml-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full focus:text-white focus:outline-hidden',
            badgeRemoveButtonClasses[color]
          )}
        >
          <span className='sr-only'>Remove</span>
          <svg className='h-2 w-2' stroke='currentColor' fill='none' viewBox='0 0 8 8'>
            <path strokeLinecap='round' strokeWidth='1.5' d='M1 1l6 6m0-6L1 7' />
          </svg>
        </button>
      )}
    </span>
  )
}

export const getStylesByColorName = (color: string): CSSProperties => {
  const badgeColor = Object.entries(BADGE_COLORS).find(([, colorName]) => colorName === color)?.[0]
  if (!badgeColor) return {}

  const baseColor = new TinyColor(badgeColor)

  const textColor = mostReadable(baseColor, ['#FFFFFF', '#111827'])
  return {
    color: textColor?.toRgbString(),
    backgroundColor: baseColor.toRgbString(),
  }
}
